import React, { useState, useEffect } from 'react';
import CreateAccount from '../auth/CreateAccount';
import LoginAccount from '../auth/LoginAccount';
import ForgotPassword from '../auth/ForgotPassword';
import { AuthRedirectIntent } from '../../types';
import { trackRegistrationPopupOpened } from '../../utils/analytics';
import { useGrowthbook } from '../../hooks/useGrowthbook';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'signup' | 'login' | 'forgot';
  videoUrl?: string;
  redirectIntent: AuthRedirectIntent;
}

type AuthView = 'signup' | 'login' | 'forgot';

const DEFAULT_VIDEO =
  'https://cdnsm.swipey.ai/prelander/createsexfriend/Realistic/auth_realistic.mp4';

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialView = 'signup',
  videoUrl,
  redirectIntent,
}) => {
  const [authView, setAuthView] = useState<AuthView>(initialView);
  const { features } = useGrowthbook();
  const isMagicLink = features.magicLink === 'ON';

  useEffect(() => {
    setAuthView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Fires once per open, regardless of entry point (CTA, girl card, etc.).
      trackRegistrationPopupOpened();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSwitchToLogin = () => setAuthView('login');
  const handleSwitchToSignup = () => setAuthView('signup');
  const handleSwitchToForgot = () => setAuthView('forgot');

  const finalVideoUrl = videoUrl || DEFAULT_VIDEO;
  // const posterUrl = 'https://cdnsm.swipey.ai/prelander/createsexfriend/Realistic/first_frame_realistic.jpg';

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/30 flex justify-center items-center z-[1000]">
      {/* Video Background */}
      <div className="fixed top-0 left-0 w-screen h-screen z-[-1]">
        <video
          src={finalVideoUrl}
          // poster={posterUrl}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        />
      </div>

      {authView === 'signup' && isMagicLink && (
        <img
          src="/assets/Logo.svg"
          alt="Swipey"
          className="absolute top-6 left-1/2 -translate-x-1/2 h-6 w-auto z-10"
        />
      )}

      {/* Auth Modal */}
      <div className="relative bg-black/20 backdrop-blur-[10px] border border-white/10 rounded-[20px] w-[95%] max-w-[450px] overflow-visible shadow-[0_20px_40px_rgba(0,0,0,0.3)]">
        {/* Auth Content */}
        <div className="flex flex-col">
          <div className="flex-1 p-[30px_24px] flex items-center justify-center">
            {authView === 'signup' && (
              <CreateAccount
                onContinue={handleSwitchToLogin}
                onClose={onClose}
                redirectIntent={redirectIntent}
              />
            )}
            {authView === 'login' && (
              <LoginAccount
                onSwitchToSignup={handleSwitchToSignup}
                onSwitchToForgot={handleSwitchToForgot}
                onClose={onClose}
                redirectIntent={redirectIntent}
              />
            )}
            {authView === 'forgot' && (
              <ForgotPassword
                onBackToLogin={handleSwitchToLogin}
                onClose={onClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
